import { formatTimeAgoMs } from "@breakout/shared/utils/time"
import { formatCompactNumber } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import { Eye, Heart, MessageCircle, Quote, Repeat2 } from "lucide-react"
import { memo } from "react"
import { SimpleAvatar } from "../ui/SimpleAvatar"
import { XLogo } from "../ui/XLogo"

interface TweetCardProps {
	tweetId: string
	handle: string
	text: string
	createdAtMs: number
	likeCount: number
	retweetCount: number
	replyCount: number
	quoteCount: number
	viewCount: number
	avatarUrl: string | null
	displayName: string | null
	marketAddress: string | null
}

export const TweetCard = memo(function TweetCard({
	tweetId,
	handle,
	text,
	createdAtMs,
	likeCount,
	retweetCount,
	replyCount,
	quoteCount,
	viewCount,
	avatarUrl,
	displayName,
	marketAddress,
}: TweetCardProps) {
	const timeAgo = formatTimeAgoMs(createdAtMs)
	const tweetUrl = `https://x.com/${handle}/status/${tweetId}`
	const creatorName = displayName ?? handle

	return (
		<article className="rounded-xl border border-border-subtle bg-bg-card transition-all duration-200 hover:border-border hover:shadow-sm">
			{/* Creator header with avatar */}
			<div className="flex items-start gap-3 px-4 pt-4">
				<a
					className="shrink-0"
					href={`https://x.com/${handle}`}
					rel="noopener noreferrer"
					target="_blank"
				>
					<SimpleAvatar
						alt={`${creatorName}'s avatar`}
						className="size-10"
						size="md"
						src={avatarUrl ?? `https://unavatar.io/twitter/${handle}`}
						variant="circle"
					/>
				</a>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5">
						<a
							className="truncate font-semibold text-sm text-text-primary transition-colors hover:text-breakout-brand"
							href={`https://x.com/${handle}`}
							rel="noopener noreferrer"
							target="_blank"
						>
							{creatorName}
						</a>
						<span className="shrink-0 text-text-muted text-xs">@{handle}</span>
						<span className="shrink-0 text-text-muted text-xs">&middot;</span>
						<a
							className="shrink-0 text-text-muted text-xs tabular-nums transition-colors hover:text-text-secondary"
							href={tweetUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							{timeAgo}
						</a>
					</div>

					{/* Tweet text */}
					<p className="mt-1 whitespace-pre-line text-sm text-text-primary leading-relaxed sm:text-[15px]">
						{text}
					</p>
				</div>
			</div>

			{/* Engagement metrics + optional Trade CTA */}
			<div className="mt-3 flex items-center gap-2 border-border-subtle/60 border-t px-4 py-2.5">
				<div className="flex flex-1 items-center gap-x-3 text-text-muted text-xs sm:gap-x-5">
					<span className="inline-flex items-center gap-1 text-text-muted">
						<XLogo aria-hidden="true" className="size-3" />
						<span className="hidden font-medium text-[11px] sm:inline">Stats</span>
					</span>
					<MetricBadge icon={Heart} label="Likes" value={likeCount} />
					<MetricBadge icon={Repeat2} label="Retweets" value={retweetCount} />
					<span className="hidden sm:inline-flex">
						<MetricBadge icon={Quote} label="Quotes" value={quoteCount} />
					</span>
					<MetricBadge icon={MessageCircle} label="Replies" value={replyCount} />
					{viewCount > 0 && <MetricBadge icon={Eye} label="Views" value={viewCount} />}
				</div>

				{marketAddress && (
					<Link
						className="shrink-0 rounded-lg bg-breakout-brand px-3 py-1.5 font-semibold text-black text-xs shadow-glow transition-all hover:bg-breakout-brand-hover active:scale-[0.98]"
						params={{ marketId: marketAddress }}
						to="/markets/$marketId"
					>
						Trade
					</Link>
				)}
			</div>
		</article>
	)
})

interface MetricBadgeProps {
	icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
	label: string
	value: number
}

function MetricBadge({ icon: Icon, label, value }: MetricBadgeProps) {
	return (
		<span className="inline-flex items-center gap-1 text-text-muted">
			<Icon aria-hidden className="size-3.5" />
			<span className="sr-only">{label}:</span>
			<span className="tabular-nums">{formatCompactNumber(value)}</span>
		</span>
	)
}
